import React from 'react';

// These badge/label helpers are only used within this row, so they live
// here (module scope, not re-created per render) instead of being shared
// with LeaveManagement.jsx's leaveHelpers.jsx — the label copy differs
// intentionally between the two screens (this one omits the emoji-heavy
// "🤒 Sick" style used in the management view and uses plain "Sick Leave"
// text instead), so consolidating them would change existing display text.

const getStatusBadge = (status) => {
  const badges = {
    pending: { class: 'status-pending', text: 'Pending' },
    approved: { class: 'status-approved', text: 'Approved' },
    rejected: { class: 'status-rejected', text: 'Rejected' }
  };
  const badge = badges[status] || badges.pending;
  return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
};

const getLeaveTypeLabel = (type) => {
  const labels = {
    sick: 'Sick Leave',
    casual: 'Casual Leave',
    emergency: 'Emergency Leave',
    informed: 'Informed Leave',
    uninformed: 'Uninformed Leave',
    other: 'Other'
  };
  return labels[type] || type;
};

const getPaymentStatusBadge = (paymentStatus) => {
  if (paymentStatus === 'paid') {
    return <span className="payment-badge paid">💰 Paid</span>;
  } else if (paymentStatus === 'unpaid') {
    return <span className="payment-badge unpaid">🚫 Unpaid</span>;
  } else {
    return <span className="payment-badge pending">⏳ Pending</span>;
  }
};

/**
 * Single row in "My Leave Requests" (LeaveRequest.jsx). This list is
 * naturally bounded to one employee's own requests, so it is intentionally
 * NOT virtualized — just memoized so unrelated state changes in the parent
 * (e.g. typing in the leave form) don't re-render every request.
 */
function MyLeaveRequestItem({ leave, onDelete }) {
  return (
    <div className={`request-item request-item--${leave.status}`}>
      <div className="request-header">
        <div>
          <strong>{getLeaveTypeLabel(leave.leaveType)}</strong>
          <span className="leave-count"> ({leave.leaveCount} day{leave.leaveCount > 1 ? 's' : ''})</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {getStatusBadge(leave.status)}
          {leave.status === 'approved' && getPaymentStatusBadge(leave.leavePaymentStatus)}
        </div>
      </div>
      <div className="request-body">
        <p><strong>Reason:</strong> {leave.reason}</p>
        <p className="request-date">
          Submitted on: {new Date(leave.createdAt).toLocaleDateString()} at {new Date(leave.createdAt).toLocaleTimeString()}
        </p>
        {leave.reviewedBy && (
          <p className="reviewer-info">
            Reviewed by: {leave.reviewedBy.firstName} {leave.reviewedBy.lastName} on {new Date(leave.reviewedAt).toLocaleDateString()}
          </p>
        )}
        {leave.rejectionReason && (
          <div className="rejection-reason">
            <strong>Rejection Reason:</strong> {leave.rejectionReason}
          </div>
        )}
      </div>
      {leave.status === 'pending' && (
        <div className="request-actions">
          <button
            className="btn-delete"
            onClick={() => onDelete(leave._id)}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default React.memo(MyLeaveRequestItem);
